import { FC, useContext, useEffect, useMemo } from "react";
import { EnterpriseComplianceDashboardContext } from "../contexts/EcdContext";
import { EcdTilesEnum, TimeFrameOptions } from "../interfaces";
import EnterpriseComplianceDashboardTimeFrame from "../components/EcdTimeFrame";
import { filterToTimeFrame } from "../utility";
import { EnterpriseComplianceFiltersContext } from "../contexts/EcdFiltersContext";
import { EcdColorsContext } from "../contexts/EcdColorsContext";
import GapOverviewChart from "../components/GapOverviewChart";
import DashboardTile from "@app/shared/qes-standards/DashboardTile";

const GapOverview: FC = () => {
    const filtersContext = useContext(EnterpriseComplianceFiltersContext);
    const dashboardContext = useContext(EnterpriseComplianceDashboardContext);
    const colorsContext = useContext(EcdColorsContext);

    const slicedGapOverviewData = useMemo(() => {
        return filterToTimeFrame(dashboardContext.gapOverviewData, filtersContext.gapOverviewTimeFrame, 4);
    }, [dashboardContext.gapOverviewData, filtersContext.gapOverviewTimeFrame]);

    /** Gap Overview's position on the page/in the DOM changes at 1200px viewport width.
     * This causes the component to unmount and remount, which would default it back into its loading
     * state without logical safeguards based on the context in the next two useEffects.
     * We could also use the CSS order property to rearrange the widgets, but that presents
     * accessibility issues with code order and visual order being out of sync.
     */
    useEffect(() => {
        if (!dashboardContext.gapOverviewData) {
            dashboardContext.updateIsTileLoadingMap(EcdTilesEnum.GapOverview, true);
        }
    }, []);

    useEffect(() => {
        if (!!filtersContext.apiReadyFilters && filtersContext.isDashboardLoading) {
            dashboardContext.updateIsTileLoadingMap(EcdTilesEnum.GapOverview, true);
        }
    }, [filtersContext.apiReadyFilters]);

    useEffect(() => {
        if (slicedGapOverviewData && dashboardContext.adequacyBreakdownData) {
            dashboardContext.updateIsTileLoadingMap(EcdTilesEnum.GapOverview, false);
        }
    }, [dashboardContext.gapOverviewData, dashboardContext.adequacyBreakdownData]);

    return (
        <DashboardTile
            headerElement={<EnterpriseComplianceDashboardTimeFrame
                disabled={dashboardContext.isTileLoadingMap.get(EcdTilesEnum.GapOverview)}
                onChange={(value: string) => filtersContext.setGapOverviewTimeFrame(value as TimeFrameOptions)}
                selectedKey={filtersContext.gapOverviewTimeFrame}
                tileKey="gap-overview"
            />}
            loading={filtersContext.isDashboardLoading ||
                dashboardContext.isTileLoadingMap.get(EcdTilesEnum.GapOverview) ||
                dashboardContext.isTileLoadingMap.get(EcdTilesEnum.AdequacyBreakdown)}
            title="Gap Overview"
            widthClass="col-12 col-xl-6"
        >
          <GapOverviewChart
            gapOverviewData={slicedGapOverviewData}
            adequacyBreakdownData={dashboardContext.adequacyBreakdownData}
            barHoverBackground={colorsContext.barHoverBackground }
            gapsArray={colorsContext.gapsArray}
            isExport={false}
          />
        </DashboardTile>
    )
}

export default GapOverview;